import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  GraduationCap, BookOpen, ArrowLeft, Play, Award, Clock,
  Sparkles, ChevronRight, Flame, LogIn,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const ACCENT = "#22D3EE";

function getAuthConfig() {
  const tok = localStorage.getItem("ai_tools_token");
  return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
}

function loadLocalEnrollments() {
  try {
    return JSON.parse(localStorage.getItem("shansolearn_enrollments") || "{}");
  } catch {
    return {};
  }
}

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const tok = localStorage.getItem("ai_tools_token");
    if (!tok) {
      // Fall back to local enrollments only (magic-link learners who never signed in)
      const local = loadLocalEnrollments();
      const fallback = Object.values(local || {}).map((e) => ({
        slug: e.slug,
        token: e.token,
        full_name: e.full_name,
        email: e.email,
        course_id: e.course_id,
        course_title: e.course_title,
        course_cover: e.course_cover,
        tier: "unknown",
      }));
      setItems(fallback);
      setLoading(false);
      return;
    }
    setSignedIn(true);
    axios.get(`${API}/my/course-enrollments`, getAuthConfig())
      .then((r) => {
        const remote = r.data.items || [];
        // Merge with local map so magic-link enrollments aren't lost
        const local = loadLocalEnrollments();
        const remoteSlugs = new Set(remote.map((it) => it.slug));
        const localOnly = Object.values(local).filter((e) => !remoteSlugs.has(e.slug)).map((e) => ({
          slug: e.slug,
          token: e.token,
          full_name: e.full_name,
          email: e.email,
          course_id: e.course_id,
          course_title: e.course_title,
          course_cover: e.course_cover,
          tier: "unknown",
        }));
        setItems([...remote, ...localOnly]);
        setUser(r.data.user || null);
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "Failed to load your courses");
      })
      .finally(() => setLoading(false));
  }, []);

  // 2. Fetch progress for each enrolled course
  const [progressMap, setProgressMap] = useState({});
  useEffect(() => {
    if (!items.length) return;
    let cancelled = false;
    (async () => {
      const results = await Promise.all(
        items.map((it) =>
          it.token
            ? axios.get(`${API}/learn/${it.slug}?token=${it.token}`)
                .then((r) => [it.slug, r.data])
                .catch(() => [it.slug, null])
            : Promise.resolve([it.slug, null])
        )
      );
      if (cancelled) return;
      const m = {};
      results.forEach(([s, d]) => { if (d) m[s] = d; });
      setProgressMap(m);
    })();
    return () => { cancelled = true; };
  }, [items]);

  const enriched = useMemo(() => items.map((it) => {
    const st = progressMap[it.slug];
    let progress_pct = 0;
    let modules_done = 0;
    let resume_module_no = null;
    let resume_module_title = null;
    let status = "enrolled";
    if (st && st.modules) {
      // Same "done" rule as the learn page: quiz passed (if quiz) or video ≥ 95%.
      const isDone = (m) => m.has_quiz ? !!m.quiz_passed : (m.video_watched_pct || 0) >= 95;
      const total = st.modules.length || 1;
      modules_done = st.modules.filter(isDone).length;
      progress_pct = Math.round((modules_done / total) * 100);
      // Resume = first unlocked-but-not-done module (LearnPage auto-selects the same one).
      const next = st.modules.find((m) => m.unlocked && !isDone(m));
      if (next) {
        resume_module_no = next.module_no;
        resume_module_title = next.title;
      }
      if (st.exam_passed || st.certificate_id) status = "completed";
      else if (modules_done > 0 || (st.modules[0]?.video_watched_pct || 0) > 5) status = "in_progress";
    }
    return { ...it, _st: st || null, progress_pct, modules_done, resume_module_no, resume_module_title, status };
  }), [items, progressMap]);

  const inProgress = enriched.filter((c) => c.status === "in_progress" || c.status === "enrolled");
  const completed = enriched.filter((c) => c.status === "completed");

  return (
    <div className="min-h-screen text-white" style={{ background: "radial-gradient(ellipse at top, #0c1424 0%, #050810 70%)" }}>
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-md border-b border-white/5" style={{ background: "rgba(5, 8, 16, 0.85)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <button
            onClick={() => navigate("/courses")}
            className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white transition text-sm"
            data-testid="my-courses-back-btn"
          >
            <ArrowLeft size={16} /> Catalog
          </button>
          <div className="flex items-center gap-2">
            <GraduationCap size={20} style={{ color: ACCENT }} />
            <span className="font-bold text-white text-sm sm:text-base">My Courses</span>
          </div>
          <Link to="/courses" className="text-xs sm:text-sm text-zinc-300 hover:text-white" data-testid="my-courses-browse-link">
            Browse all <ChevronRight size={14} className="inline" />
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="mb-8 sm:mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold mb-3" style={{ background: "rgba(34,211,238,0.12)", color: ACCENT, border: "1px solid rgba(34,211,238,0.25)" }}>
            <Sparkles size={12} /> Your learning hub
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {user?.name ? <>Welcome back, <span style={{ color: ACCENT }}>{user.name.split(" ")[0]}</span>.</> : "Your enrolled courses"}
          </h1>
          <p className="mt-2 text-zinc-400 text-sm sm:text-base max-w-2xl">
            All your ShansoLearn flagships and Quick Bites in one place. Pick up where you left off — your progress, quizzes and certificates are synced across devices.
          </p>
        </div>

        {/* Loading / empty / signed-out states */}
        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-zinc-400" data-testid="my-courses-loading">Loading your courses…</div>
        )}

        {!loading && !signedIn && items.length === 0 && (
          <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-cyan-900/15 via-zinc-900/40 to-zinc-900/20 p-8 sm:p-12 text-center" data-testid="my-courses-signed-out">
            <LogIn size={36} className="mx-auto mb-4" style={{ color: ACCENT }} />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">Sign in to see all your courses</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              We sync your enrollments across devices when you sign in with the email you used at checkout. No password needed.
            </p>
            <Link to="/courses" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm" style={{ background: ACCENT, color: "#0a0e1a" }} data-testid="my-courses-signin-btn">
              Browse courses <ChevronRight size={16} />
            </Link>
            <p className="text-xs text-zinc-500 mt-4">If you paid recently, check the email we sent — it has your access link.</p>
          </div>
        )}

        {!loading && items.length === 0 && signedIn && (
          <div className="rounded-2xl border border-white/10 bg-white/5 p-8 sm:p-12 text-center" data-testid="my-courses-empty">
            <BookOpen size={36} className="mx-auto mb-4 text-zinc-500" />
            <h2 className="text-xl sm:text-2xl font-bold mb-2">No courses yet</h2>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              You haven&apos;t enrolled in any course on this account ({user?.email}). Browse the catalog and pick your first one.
            </p>
            <Link to="/courses" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold text-sm" style={{ background: ACCENT, color: "#0a0e1a" }} data-testid="my-courses-browse-btn">
              Browse the catalog <ChevronRight size={16} />
            </Link>
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-red-300 text-sm mb-6" data-testid="my-courses-error">
            {error}
          </div>
        )}

        {/* In Progress + Enrolled */}
        {inProgress.length > 0 && (
          <section className="mb-12" data-testid="my-courses-in-progress-section">
            <div className="flex items-center gap-2 mb-4">
              <Flame size={18} style={{ color: ACCENT }} />
              <h2 className="text-xl sm:text-2xl font-extrabold">Continue learning</h2>
              <span className="text-sm text-zinc-500">({inProgress.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {inProgress.map((c) => <EnrolledCard key={c.slug} c={c} />)}
            </div>
          </section>
        )}

        {/* Completed */}
        {completed.length > 0 && (
          <section data-testid="my-courses-completed-section">
            <div className="flex items-center gap-2 mb-4">
              <Award size={18} className="text-amber-400" />
              <h2 className="text-xl sm:text-2xl font-extrabold">Completed</h2>
              <span className="text-sm text-zinc-500">({completed.length})</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {completed.map((c) => <EnrolledCard key={c.slug} c={c} />)}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}

function EnrolledCard({ c }) {
  const isQuickBite = c.tier === "quick_bite";
  const completed = c.status === "completed";
  const cta = completed ? "View certificate" : c.progress_pct > 0 ? "Continue" : "Start learning";
  const href = `/learn/${c.slug}?token=${c.token}`;

  return (
    <Link
      to={href}
      className="group rounded-xl overflow-hidden border border-white/10 bg-white/5 hover:border-cyan-400/40 hover:bg-white/[0.07] transition flex flex-col"
      data-testid={`enrolled-card-${c.slug}`}
    >
      <div className="relative aspect-[16/9] bg-zinc-900 overflow-hidden">
        {c.course_cover ? (
          <img src={c.course_cover} alt={c.course_title} className="w-full h-full object-cover group-hover:scale-[1.03] transition" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-700"><BookOpen size={36} /></div>
        )}
        <div className="absolute top-2 left-2 flex gap-1.5">
          {isQuickBite ? (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(245,158,11,0.18)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.4)" }}>Quick Bite</span>
          ) : (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(34,211,238,0.18)", color: ACCENT, border: "1px solid rgba(34,211,238,0.4)" }}>Flagship</span>
          )}
          {completed && (
            <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider" style={{ background: "rgba(245,158,11,0.18)", color: "#fbbf24", border: "1px solid rgba(245,158,11,0.4)" }}>Completed</span>
          )}
        </div>
      </div>
      <div className="p-4 flex-1 flex flex-col gap-2">
        <h3 className="font-bold text-white leading-snug line-clamp-2">{c.course_title || c.slug}</h3>
        {c.modules_count && (
          <p className="text-xs text-zinc-500 flex items-center gap-2">
            <BookOpen size={11} /> {c.modules_count} modules
            {c.duration_min_total ? <> · <Clock size={11} /> {Math.round(c.duration_min_total / 60 * 10) / 10}h</> : null}
          </p>
        )}
        {/* Resume chip — only when there's an active module to return to */}
        {!completed && c.resume_module_no && c.progress_pct > 0 && (
          <div
            className="inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-[11px] font-semibold border max-w-full"
            style={{ background: "rgba(34,211,238,0.08)", borderColor: "rgba(34,211,238,0.35)", color: ACCENT }}
            data-testid={`resume-chip-${c.slug}`}
          >
            <Play size={10} className="shrink-0" />
            <span className="truncate">Resume · Module {c.resume_module_no}{c.resume_module_title ? `: ${c.resume_module_title}` : ""}</span>
          </div>
        )}
        {/* Progress bar */}
        {!completed && (
          <div className="mt-1">
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div className="h-full transition-all" style={{ width: `${c.progress_pct}%`, background: ACCENT }} />
            </div>
            <p className="text-[11px] text-zinc-500 mt-1">{c.progress_pct}% complete · {c.modules_done || 0} module{c.modules_done === 1 ? "" : "s"} done</p>
          </div>
        )}
        <div className="mt-auto pt-2 flex items-center justify-between">
          <span className="text-sm font-semibold text-white inline-flex items-center gap-1.5">
            <Play size={13} style={{ color: ACCENT }} /> {cta}
          </span>
          <ChevronRight size={16} className="text-zinc-500 group-hover:text-cyan-300 transition" />
        </div>
      </div>
    </Link>
  );
}
